import React, { useState, useEffect, useRef } from 'react';
import styles from './BlogImage.module.scss';

interface BlogImageProps {
  src: string;
  alt: string;
  className?: string;
  fallbackSrc?: string;
}

// The blog carousel renders several clones of each slide for its infinite-loop
// effect (react-slick), so the same image src mounts multiple times. Without
// this cache, each mounted instance below would independently fetch() and
// blob the same remote image, multiplying network requests by the clone count.
const blobUrlCache = new Map<string, Promise<string>>();

const getCachedObjectUrl = (src: string): Promise<string> => {
  let entry = blobUrlCache.get(src);
  if (!entry) {
    entry = fetch(src, { method: 'GET', credentials: 'omit' }).then(async (response) => {
      if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
      const blob = await response.blob();
      let finalBlob = blob;
      if (!blob.type || blob.type === 'application/octet-stream') {
        const urlLower = src.toLowerCase();
        let mimeType = 'image/jpeg';
        if (urlLower.includes('.png')) mimeType = 'image/png';
        else if (urlLower.includes('.gif')) mimeType = 'image/gif';
        else if (urlLower.includes('.webp')) mimeType = 'image/webp';
        finalBlob = new Blob([blob], { type: mimeType });
      }
      return URL.createObjectURL(finalBlob);
    });
    blobUrlCache.set(src, entry);
    entry.catch(() => blobUrlCache.delete(src));
  }
  return entry;
};

const BlogImage: React.FC<BlogImageProps> = ({
  src,
  alt,
  className = '',
  fallbackSrc = 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&q=80',
}) => {
  const [imageSrc, setImageSrc] = useState(src);
  const [hasError, setHasError] = useState(false);
  const triedOriginalRef = useRef(false);

  // Try to load image as blob if it's from media/view API. The blob/object URL
  // itself is cached by src in getCachedObjectUrl above, so carousel clones of
  // the same slide share one network fetch instead of issuing one each.
  useEffect(() => {
    setHasError(false);
    triedOriginalRef.current = false;

    if (src && src.includes('/media/view?')) {
      let cancelled = false;
      getCachedObjectUrl(src)
        .then((url) => {
          if (!cancelled) setImageSrc(url);
        })
        .catch((error) => {
          console.warn('Failed to load image as blob, using direct URL:', error);
          if (!cancelled) setImageSrc(src);
        });
      return () => {
        cancelled = true;
      };
    }

    setImageSrc(src);
  }, [src]);

  const handleError = () => {
    if (hasError) return;

    // The shared object URL failed to render - try the original src once before
    // giving up on the remote image entirely.
    if (!triedOriginalRef.current && src && src !== imageSrc) {
      triedOriginalRef.current = true;
      setImageSrc(src);
      return;
    }

    console.warn('Image failed to load:', imageSrc);
    setHasError(true);
    setImageSrc(fallbackSrc);
  };

  const handleLoad = () => {
    setHasError(false);
  };

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      onError={handleError}
      onLoad={handleLoad}
      loading="lazy"
    />
  );
};

export default BlogImage;
